// Underdog bonus on the map (Design §6.5): the drone and its rounds, the zombie, the divine
// light. The rules are in the simulation (`sim::underdog`); this only draws them.

/** Tank floats (`TANK_STRIDE`): drone rounds left, its latest target + 1, risen zombie, blessed. */
export const DRONE = 23;
const DRONE_SHOT = 24;
export const ZOMBIE = 25;
export const BLESSED = 26;
/** Seconds until it rises as a zombie (0 = not about to). */
export const RISING = 27;

/** Paint of a risen zombie: rotten green. */
export const ZOMBIE_COLOR = '#6f8f4e';
/** Bonus names by wire value (`Bonus::to_u8`), for the shop's plaque. */
export const BONUS_NAMES = ['', 'Drone', 'Zombie', 'Divine aid'];

const TRACER_MS = 70; // a round's flight on screen
const ROTOR_SPIN_MS = 60;

/** Where the drone of the tank at `o` hovers: `side` to its right (`sim::underdog::drone_pos`). */
export function dronePos(t: Float32Array, o: number, side: number): [number, number] {
  const a = t[o + 2] + Math.PI / 2;
  return [t[o] + Math.cos(a) * side, t[o + 1] + Math.sin(a) * side];
}

const DRONE_FOLLOW = 7; // 1/s: how stiffly it follows its spot (critically damped: eases in and out)
const DRONE_TURN = 6; // 1/s: how fast it turns after the tank
const DRONE_WOBBLE = 3; // world units it wavers round its spot
const DRONE_SNAP = 200; // farther off (respawn, new round): it is there at once

/**
 * Where each drone is drawn: it flies after its spot beside the tank like a damped spring, a
 * little late and never in a straight jerk, and wavers round it. Only the picture; the
 * simulation fires from the spot itself.
 */
export class DroneFlight {
  private drones = new Map<number, { x: number; y: number; vx: number; vy: number; angle: number; t: number }>();

  /** Position and heading of the drone of tank `i` (`o` its offset) at `now` (ms). */
  at(i: number, t: Float32Array, o: number, side: number, now: number): [number, number, number] {
    const [sx, sy] = dronePos(t, o, side);
    const s = now / 1000;
    const tx = sx + Math.sin(s * 1.3 + i * 2.1) * DRONE_WOBBLE + Math.sin(s * 3.1 + i) * DRONE_WOBBLE * 0.4;
    const ty = sy + Math.cos(s * 1.7 + i * 1.3) * DRONE_WOBBLE + Math.sin(s * 2.6 + i * 0.7) * DRONE_WOBBLE * 0.4;
    let d = this.drones.get(i);
    if (!d || Math.hypot(tx - d.x, ty - d.y) > DRONE_SNAP || now - d.t > 500) {
      d = { x: tx, y: ty, vx: 0, vy: 0, angle: t[o + 2], t: now };
      this.drones.set(i, d);
    }
    const dt = Math.min(0.05, (now - d.t) / 1000);
    d.t = now;
    // Critically damped spring towards the target: speeds up and slows down smoothly.
    const k = DRONE_FOLLOW;
    d.vx += (k * k * (tx - d.x) - 2 * k * d.vx) * dt;
    d.vy += (k * k * (ty - d.y) - 2 * k * d.vy) * dt;
    d.x += d.vx * dt;
    d.y += d.vy * dt;
    const turn = Math.atan2(Math.sin(t[o + 2] - d.angle), Math.cos(t[o + 2] - d.angle));
    d.angle += turn * Math.min(1, DRONE_TURN * dt);
    return [d.x, d.y, d.angle];
  }

  /** Where it was drawn last (for its rounds), else its spot. */
  last(i: number, t: Float32Array, o: number, side: number): [number, number] {
    const d = this.drones.get(i);
    return d ? [d.x, d.y] : dronePos(t, o, side);
  }
}

/** A small quadcopter: shadow, X frame, four spinning rotors, a red eye towards the front. */
export function drawDrone(ctx: CanvasRenderingContext2D, x: number, y: number, angle: number, r: number, now: number): void {
  const size = r * 0.55;
  const bob = Math.sin(now / 300) * 1.5;
  ctx.save();
  ctx.fillStyle = 'rgba(0, 0, 0, 0.25)';
  ctx.beginPath();
  ctx.ellipse(x + 4, y + 6, size, size * 0.7, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.translate(x, y + bob);
  ctx.rotate(angle);
  ctx.strokeStyle = '#2b2f26';
  ctx.lineWidth = 2;
  ctx.beginPath();
  for (const s of [1, -1]) {
    ctx.moveTo(-size, -size * s);
    ctx.lineTo(size, size * s);
  }
  ctx.stroke();
  for (const [dx, dy] of [[1, 1], [1, -1], [-1, 1], [-1, -1]]) {
    const [cx, cy] = [dx * size, dy * size];
    ctx.fillStyle = 'rgba(200, 205, 190, 0.35)';
    ctx.beginPath();
    ctx.arc(cx, cy, size * 0.55, 0, Math.PI * 2);
    ctx.fill();
    const spin = (now / ROTOR_SPIN_MS) * dx * dy;
    ctx.strokeStyle = '#1b1d18';
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(cx + Math.cos(spin) * size * 0.5, cy + Math.sin(spin) * size * 0.5);
    ctx.lineTo(cx - Math.cos(spin) * size * 0.5, cy - Math.sin(spin) * size * 0.5);
    ctx.stroke();
  }
  ctx.fillStyle = '#4a5040';
  ctx.strokeStyle = '#1b1d18';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.roundRect(-size * 0.45, -size * 0.35, size * 0.9, size * 0.7, 2);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = '#e03020';
  ctx.beginPath();
  ctx.arc(size * 0.35, 0, 1.4, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/** Drone rounds in flight: a black pixel from the drone to its target, one per round fired. */
export class DroneShots {
  private left: number[] = [];
  private shots: { from: [number, number]; to: [number, number]; start: number }[] = [];

  /** New rounds since the last frame (the count of a drone went down). */
  update(tanks: Float32Array, stride: number, from: (i: number, o: number) => [number, number], now: number): void {
    this.shots = this.shots.filter((s) => now - s.start < TRACER_MS);
    for (let i = 0; i < tanks.length / stride; i++) {
      const o = i * stride;
      const left = tanks[o + DRONE];
      const target = tanks[o + DRONE_SHOT] - 1;
      if (left < (this.left[i] ?? 0) && target >= 0) {
        const t = target * stride;
        this.shots.push({ from: from(i, o), to: [tanks[t], tanks[t + 1]], start: now });
      }
      this.left[i] = left;
    }
  }

  draw(ctx: CanvasRenderingContext2D, now: number): void {
    ctx.save();
    ctx.fillStyle = '#000';
    for (const s of this.shots) {
      const q = Math.min(1, (now - s.start) / TRACER_MS);
      ctx.fillRect(s.from[0] + (s.to[0] - s.from[0]) * q - 0.75, s.from[1] + (s.to[1] - s.from[1]) * q - 0.75, 1.5, 1.5);
    }
    ctx.restore();
  }
}

/** Over a zombie's hull: stitches, rotten patches and two glowing eyes on the turret. */
export function drawZombie(ctx: CanvasRenderingContext2D, x: number, y: number, angle: number, r: number, now: number): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(angle);
  ctx.fillStyle = 'rgba(45, 30, 18, 0.6)';
  for (const [px, py, pr] of [[-0.55, 0.45, 0.24], [-0.2, -0.6, 0.18], [0.5, 0.55, 0.16], [0.6, -0.5, 0.12]]) {
    ctx.beginPath();
    ctx.ellipse(px * r, py * r, pr * r, pr * r * 0.7, px, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.strokeStyle = '#1d140c';
  ctx.lineWidth = 1.1;
  ctx.beginPath();
  ctx.moveTo(-0.85 * r, -0.2 * r);
  ctx.lineTo(-0.15 * r, 0.75 * r);
  for (let k = 1; k < 5; k++) {
    // Stitches across the scar.
    const q = k / 5;
    const [sx, sy] = [-0.85 * r + 0.7 * r * q, -0.2 * r + 0.95 * r * q];
    ctx.moveTo(sx - 0.1 * r, sy + 0.07 * r);
    ctx.lineTo(sx + 0.1 * r, sy - 0.07 * r);
  }
  ctx.stroke();
  const glow = 0.6 + 0.4 * Math.sin(now / 250);
  ctx.fillStyle = `rgba(190, 255, 90, ${glow})`;
  ctx.shadowColor = '#b8ff5a';
  ctx.shadowBlur = 6;
  for (const s of [-1, 1]) {
    ctx.beginPath();
    ctx.arc(0.22 * r, s * 0.18 * r, 0.13 * r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
  // Flies circling over it.
  ctx.save();
  ctx.fillStyle = '#111';
  for (let k = 0; k < 3; k++) {
    const a = now / (260 + 70 * k) + k * 2.1;
    const d = r * (1.05 + 0.25 * Math.sin(now / 410 + k));
    ctx.beginPath();
    ctx.arc(x + Math.cos(a) * d, y + Math.sin(a * 1.3) * d * 0.8, 1.3, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

/** Divine aid: a soft beam of light from the sky onto the tank, with a bright pool round it. */
export function drawBeam(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, now: number): void {
  const flicker = 0.85 + 0.15 * Math.sin(now / 140);
  const top = y - 14 * r;
  ctx.save();
  // Golden, see-through: the tank stays clear under it.
  const beam = ctx.createLinearGradient(0, top, 0, y);
  beam.addColorStop(0, 'rgba(255, 236, 160, 0)');
  beam.addColorStop(0.6, `rgba(255, 236, 160, ${0.18 * flicker})`);
  beam.addColorStop(1, `rgba(255, 240, 180, ${0.32 * flicker})`);
  ctx.fillStyle = beam;
  ctx.beginPath();
  ctx.moveTo(x - 0.8 * r, top);
  ctx.lineTo(x - 1.5 * r, y);
  ctx.ellipse(x, y, 1.5 * r, 0.8 * r, 0, Math.PI, 0, true);
  ctx.lineTo(x + 0.8 * r, top);
  ctx.closePath();
  ctx.fill();
  const pool = ctx.createRadialGradient(x, y, 0, x, y, 1.8 * r);
  pool.addColorStop(0, `rgba(255, 238, 170, ${0.4 * flicker})`);
  pool.addColorStop(1, 'rgba(255, 238, 170, 0)');
  ctx.fillStyle = pool;
  ctx.beginPath();
  ctx.ellipse(x, y, 1.8 * r, 1.1 * r, 0, 0, Math.PI * 2);
  ctx.fill();
  // A few motes drifting down in the light.
  ctx.fillStyle = `rgba(255, 252, 230, ${0.8 * flicker})`;
  for (let k = 0; k < 6; k++) {
    const q = (now / 1800 + k / 6) % 1;
    const my = top + (y - top) * (0.5 + 0.5 * q);
    const mx = x + Math.sin(k * 2.3 + now / 600) * r * (0.4 + 0.8 * q);
    ctx.fillRect(mx - 0.8, my - 0.8, 1.6, 1.6);
  }
  ctx.restore();
}

/**
 * While a zombie is about to rise: big in the middle of the map (screen pixels `x`, `y`, text
 * height `size`), for everybody — ZOMBIE! and who it is (`name` in its colour; none: anonymous).
 */
export function drawRising(ctx: CanvasRenderingContext2D, x: number, y: number, size: number, name: string | undefined, color: string, now: number): void {
  ctx.save();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineJoin = 'round';
  const pulse = 1 + 0.06 * Math.sin(now / 90);
  ctx.font = `${Math.round(size * pulse)}px 'Black Ops One', Impact, sans-serif`;
  ctx.lineWidth = size * 0.12;
  ctx.strokeStyle = '#0b120a';
  ctx.strokeText('ZOMBIE!', x, y);
  ctx.shadowColor = '#b8ff5a';
  ctx.shadowBlur = size * 0.35;
  ctx.fillStyle = '#9be35a';
  ctx.fillText('ZOMBIE!', x, y);
  ctx.shadowBlur = 0;
  // Below: '<name> rises from the dead', the name in its player colour.
  const small = Math.round(size * 0.36);
  ctx.font = `600 ${small}px 'Oswald', 'Arial Narrow', sans-serif`;
  const rest = name ? ' rises from the dead' : 'A tank rises from the dead';
  const [wn, wr] = [name ? ctx.measureText(name).width : 0, ctx.measureText(rest).width];
  const left = x - (wn + wr) / 2;
  const ly = y + size * 0.85;
  ctx.textAlign = 'left';
  ctx.lineWidth = small * 0.18;
  ctx.strokeStyle = '#0b120a';
  if (name) {
    ctx.strokeText(name, left, ly);
    ctx.fillStyle = color;
    ctx.fillText(name, left, ly);
  }
  ctx.strokeText(rest, left + wn, ly);
  ctx.fillStyle = '#f2f0e4';
  ctx.fillText(rest, left + wn, ly);
  ctx.restore();
}
